import { Note, Folder, UserSettings } from "../../shared/types";
import { StoragePayload, safeParseNativeResponse } from "../nativeIntegration/types";
import appLogger from "../utils/logger";

const logger = appLogger.with({ namespace: "NativeDB" });

type User = {
  id: string;
  data: {
    settings: UserSettings;
  };
  isDirty: number;
};

type AudioChunk = {
  id: string;
  audioId: string;
  chunkIndex: number;
  duration: number;
  blob: Blob;
  isDirty: number;
};

// --- Bridge Transport ---

class NativeBridge {
  private nextRequestId = 0;
  private pendingRequests = new Map<number, { resolve: (v: any) => void; reject: (e: any) => void }>();

  postMessage(action: string, params: Record<string, any> = {}): Promise<any> {
    const requestId = this.nextRequestId++;
    const message = { requestId, action, ...params };

    let timeoutId: number;
    const promise = new Promise((resolve, reject) => {
      this.pendingRequests.set(requestId, { resolve, reject });
      timeoutId = window.setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          reject(new Error(`Request timed out: ${action}`));
        }
      }, 30_000);
    }).finally(() => {
      window.clearTimeout(timeoutId);
    });

    window.webkit?.messageHandlers.storage.postMessage(message);
    return promise;
  }

  handleResponse(payload: StoragePayload) {
    const { requestId, result } = payload;
    const pending = this.pendingRequests.get(requestId);
    if (!pending) {
      logger.warn("Received response for unknown request", { context: { response: payload }, report: true });
      return;
    }
    if (result.error) {
      pending.reject(result.error);
    } else {
      pending.resolve(result.data);
    }
    this.pendingRequests.delete(requestId);
  }
}

export const nativeBridge = new NativeBridge();

/**
 * The editor installs the full `window.nativeResponse` handler
 * (useNativeResponseHandler) only after sign-in. Code that reads native storage
 * before that, such as the Ideaflow ID sign-in handoff on the login page, needs
 * storage replies delivered too, or every call times out after 30 s. This
 * routes storage replies only and steps aside once the editor's handler is set.
 */
export function ensureNativeStorageResponses() {
  if (typeof window === "undefined" || window.nativeResponse) return;
  window.nativeResponse = (value: unknown) => {
    const parsed = safeParseNativeResponse(value);
    if (parsed.success && parsed.data.type === "storage") nativeBridge.handleResponse(parsed.data.payload);
  };
}

// --- Serialization Helpers ---

const DATE_FIELDS = new Set(["createdAt", "updatedAt", "deletedAt", "simonUpdatedAt", "serverUpdateTimestamp"]);

function reviveDates(key: string, value: any): any {
  if (DATE_FIELDS.has(key) && typeof value === "string") {
    return new Date(value);
  }
  return value;
}

async function blobToBase64(blob: Blob): Promise<{ base64: string; type: string }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1];
      resolve({ base64, type: blob.type });
    };
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string, type: string): Blob {
  const byteCharacters = window.atob(base64);
  const byteArray = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArray[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([byteArray], { type });
}

function parseAudioChunkResponse(row: any): AudioChunk {
  return {
    id: row.id,
    audioId: row.audioId,
    chunkIndex: row.chunkIndex,
    duration: row.duration,
    blob: base64ToBlob(row.blobBase64, row.blobType),
    isDirty: row.isDirty,
  };
}

// --- NativeDB Implementation ---

export const nativeDB = {
  notes: {
    upsert: async (notes: Note[], isDirty: boolean) => {
      const serializedNotes = notes.map((note) => ({
        id: note.id,
        data: JSON.stringify(note),
      }));
      await nativeBridge.postMessage("notes.upsert", {
        notes: serializedNotes,
        isDirty: isDirty ? 1 : 0,
      });
    },

    get: async (id: string): Promise<Note | null> => {
      const result = await nativeBridge.postMessage("notes.get", { id });
      if (!result) return null;
      return JSON.parse(result.data, reviveDates);
    },

    getAll: async (): Promise<Note[]> => {
      const results = await nativeBridge.postMessage("notes.getAll");
      return results.map((r: any) => JSON.parse(r.data, reviveDates));
    },

    getDirtyIds: async (): Promise<string[]> => {
      return await nativeBridge.postMessage("notes.getDirtyIds");
    },

    markDirty: async (ids: string[], isDirty: boolean) => {
      await nativeBridge.postMessage("notes.markDirty", {
        ids,
        isDirty: isDirty ? 1 : 0,
      });
    },
  },

  folders: {
    upsert: async (folders: Folder[], isDirty: boolean) => {
      const serializedFolders = folders.map((folder) => ({
        id: folder.id,
        data: JSON.stringify(folder),
      }));
      await nativeBridge.postMessage("folders.upsert", {
        folders: serializedFolders,
        isDirty: isDirty ? 1 : 0,
      });
    },

    get: async (id: string): Promise<Folder | null> => {
      const result = await nativeBridge.postMessage("folders.get", { id });
      if (!result) return null;
      return JSON.parse(result.data, reviveDates);
    },

    getAll: async (): Promise<Folder[]> => {
      const results = await nativeBridge.postMessage("folders.getAll");
      return results.map((r: any) => JSON.parse(r.data, reviveDates));
    },

    getDirtyIds: async (): Promise<string[]> => {
      return await nativeBridge.postMessage("folders.getDirtyIds");
    },

    markDirty: async (ids: string[], isDirty: boolean) => {
      await nativeBridge.postMessage("folders.markDirty", {
        ids,
        isDirty: isDirty ? 1 : 0,
      });
    },
  },

  users: {
    upsert: async (user: User) => {
      await nativeBridge.postMessage("users.upsert", {
        id: user.id,
        data: JSON.stringify(user.data),
        isDirty: user.isDirty ? 1 : 0,
      });
    },

    get: async (_id: string): Promise<User | null> => {
      const userId = "1";
      const result = await nativeBridge.postMessage("users.get", { id: userId });
      if (!result) return null;
      return {
        id: result.id,
        data: JSON.parse(result.data),
        isDirty: result.isDirty,
      };
    },
  },

  kv: {
    set: async (key: string, value: unknown) => {
      await nativeBridge.postMessage("kv.set", {
        key,
        value: JSON.stringify(value),
      });
    },

    get: async <T = unknown>(key: string): Promise<T | null> => {
      const result = await nativeBridge.postMessage("kv.get", { key });
      if (result === null || result === undefined) return null;
      return JSON.parse(result) as T;
    },

    remove: async (key: string) => {
      await nativeBridge.postMessage("kv.remove", { key });
    },
  },

  audioChunks: {
    insert: async (audioChunk: AudioChunk) => {
      const { base64, type } = await blobToBase64(audioChunk.blob);
      await nativeBridge.postMessage("audioChunks.insert", {
        id: audioChunk.id,
        audioId: audioChunk.audioId,
        chunkIndex: audioChunk.chunkIndex,
        duration: audioChunk.duration,
        blobBase64: base64,
        blobType: type,
        isDirty: audioChunk.isDirty ? 1 : 0,
      });
    },

    update: async (update: Pick<AudioChunk, "id" | "isDirty">) => {
      await nativeBridge.postMessage("audioChunks.update", {
        id: update.id,
        isDirty: update.isDirty ? 1 : 0,
      });
    },

    get: async (id: string): Promise<AudioChunk | null> => {
      const result = await nativeBridge.postMessage("audioChunks.get", { id });
      if (!result) return null;
      return parseAudioChunkResponse(result);
    },

    getAllByAudioId: async (audioId: string): Promise<AudioChunk[]> => {
      const results = await nativeBridge.postMessage("audioChunks.getAllByAudioId", { audioId });
      return results.map(parseAudioChunkResponse);
    },

    getAllDirty: async (): Promise<AudioChunk[]> => {
      const results = await nativeBridge.postMessage("audioChunks.getAllDirty");
      return results.map(parseAudioChunkResponse);
    },

    deleteAllByAudioId: async (audioId: string) => {
      await nativeBridge.postMessage("audioChunks.deleteAllByAudioId", { audioId });
    },

    getUploadedAudioIds: async (): Promise<string[]> => {
      return await nativeBridge.postMessage("audioChunks.getUploadedAudioIds");
    },
  },

  drop: async () => {
    await nativeBridge.postMessage("drop");
  },

  clear: async () => {
    await nativeBridge.postMessage("clear");
  },
};
